/**
 * Utility functions to generate unique, SEO-optimized meta descriptions
 * Each function creates dynamic descriptions based on entity data
 * All descriptions are under 160 characters for optimal SEO
 * No emojis, no numbers, no dates - optimized for CTR
 */

interface ListingData {
  name: string;
  type: 'gym' | 'store' | 'fitness_center' | string;
  city: string;
  rating?: number;
  short_description?: string;
  seo_description?: string;
  description?: string;
  address?: string;
  price_range?: string;
  tags?: string[];
  is_premium?: boolean;
  /** Listing slug — used to disambiguate duplicate name+city+type rows so descriptions stay unique */
  slug?: string;
}

/** Returns " (Sede N)" for slug ending in -N (N>=2), otherwise empty. Keeps descriptions unique. */
const sedeSuffix = (slug?: string): string => {
  if (!slug) return "";
  const m = slug.match(/-(\d+)$/);
  if (!m) return "";
  const n = parseInt(m[1], 10);
  if (!n || n < 2) return "";
  return ` (Sede ${n})`;
};

interface EventData {
  title: string;
  event_type: string;
  city: string;
  date: string;
  short_description?: string;
  description: string;
  price?: number | null;
  max_participants?: number | null;
}

interface TrainerData {
  full_name: string;
  location_city: string;
  bio?: string;
  specialty?: string[];
  years_experience?: number;
  hourly_rate?: number;
  clients_trained?: number;
  certifications?: string[];
}

interface SportProfessionalData {
  full_name: string;
  category: string;
  ranking: number;
  location_country: string;
  location_city?: string;
  bio?: string;
  years_experience?: number;
  clients_trained?: number;
  achievements?: string[];
  /** Unique slug — disambiguates duplicate name+category+country rows */
  slug?: string;
}

/** Returns a humanised qualifier from the part of slug that differs from the name slug. */
const slugQualifierTail = (slug?: string, primaryName?: string): string => {
  if (!slug) return "";
  const toSlug = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const nameSlug = primaryName ? toSlug(primaryName) : "";
  let tail = nameSlug && slug.startsWith(nameSlug)
    ? slug.slice(nameSlug.length).replace(/^-/, "")
    : "";
  if (!tail) {
    const m = slug.match(/-(\d+)$/);
    return m && parseInt(m[1], 10) >= 2 ? ` (perfil #${m[1]})` : "";
  }
  if (/^\d+$/.test(tail)) return ` (perfil #${tail})`;
  const human = tail.split("-").filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  return ` (${human})`;
};

interface VideoData {
  titulo: string;
  descripcion?: string;
  categoria?: string;
  vistas?: number;
  votos_positivos?: number;
  creado_en?: string;
  ciudad?: string;
  duracion?: string;
}

interface ArticleData {
  title: string;
  meta_description?: string;
  summary?: string;
  tags?: string[];
  reading_time?: number;
  category?: string;
  votos_positivos?: number;
  publish_date?: string;
  created_at?: string;
}

/**
 * Generates unique meta descriptions for listings (gyms, stores, fitness centers)
 * Name first, no emojis, no numbers - optimized for CTR
 */
export const generateListingMetaDescription = (listing: ListingData): string => {
  const typeMap: Record<string, string> = {
    gym: 'Gimnasio',
    store: 'Tienda deportiva',
    fitness_center: 'Centro fitness'
  };

  const typeName = typeMap[listing.type] || 'Centro deportivo';
  const sede = sedeSuffix(listing.slug);

  // Use seo_description if available and well-crafted
  if (listing.seo_description && listing.seo_description.length > 50) {
    const base = listing.seo_description.trim();
    // Append sede suffix if it would still fit; ensure base is unique per row
    const withSede = sede && !base.includes(sede.trim()) ? `${base}${sede}` : base;
    return withSede.substring(0, 160);
  }

  const compose = (body: string) => {
    const withSede = sede ? `${listing.name}${sede} ${body}` : `${listing.name} ${body}`;
    return withSede.substring(0, 160);
  };

  // Format 1: Premium listings
  if (listing.is_premium && listing.rating) {
    return compose(`en ${listing.city} - ${typeName} TOP valorado. ${listing.tags?.slice(0, 2).join(' y ') || 'Resultados garantizados'}. Instalaciones premium, profesionales certificados.`);
  }

  // Format 2: High rating
  if (listing.rating && listing.rating >= 4.5) {
    return compose(`${listing.city} - ${typeName} líder con las mejores valoraciones. ${listing.tags?.slice(0, 2).join(', ') || 'Instalaciones premium'}. Reserva tu primera visita gratis.`);
  }

  // Format 3: Tags available
  if (listing.tags && listing.tags.length > 1) {
    return compose(`en ${listing.city} - Expertos en ${listing.tags[0]} y ${listing.tags[1]}. ${typeName} profesional con resultados comprobados. Primera clase gratis.`);
  }

  // Format 4: Address available
  if (listing.address) {
    return compose(`- ${typeName} en ${listing.address.split(',')[0]}, ${listing.city}. Equipamiento profesional y clases incluidas. Ven a conocernos sin compromiso.`);
  }

  // Format 5: Short description available
  if (listing.short_description) {
    const cleanDesc = listing.short_description.substring(0, 70);
    return compose(`${listing.city}: ${cleanDesc}. ${typeName} verificado con instalaciones de primera. Visita gratis.`);
  }

  // Format 6: Generic
  return compose(`- El mejor ${typeName.toLowerCase()} de ${listing.city}. Instalaciones modernas, resultados reales. Primera sesión gratis.`);
};

/**
 * Generates unique meta descriptions for events
 * Title first, no dates, no numbers - optimized for urgency
 */
export const generateEventMetaDescription = (event: EventData): string => {
  // Use short_description if compelling
  if (event.short_description && event.short_description.length > 50 && event.short_description.length <= 160) {
    return event.short_description;
  }
  
  // Format 1: Free event
  if (event.price === 0 || event.price === null) {
    return `${event.title} en ${event.city} - ${event.event_type} gratuito. Plazas limitadas, inscripción abierta. Evento deportivo imprescindible.`.substring(0, 160);
  }
  
  // Format 2: Paid event with participants
  if (event.price && event.price > 0 && event.max_participants) {
    return `${event.title} ${event.city} - ${event.event_type} deportivo. Plazas limitadas. Inscripción online disponible. No te pierdas este evento único.`.substring(0, 160);
  }
  
  // Format 3: Paid event
  if (event.price && event.price > 0) {
    return `${event.title} - ${event.event_type} TOP en ${event.city}. Inscripción online. No te quedes fuera de este evento deportivo.`.substring(0, 160);
  }
  
  // Format 4: Generic
  const cleanDesc = event.description.replace(/<[^>]*>/g, '').substring(0, 80);
  return `${event.title} en ${event.city}: ${cleanDesc}. Inscríbete y vive una experiencia deportiva inolvidable.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for trainer profiles
 * Name first, no numbers/emojis - emphasizes results and value
 */
export const generateTrainerMetaDescription = (trainer: TrainerData): string => {
  // Format 1: Specialty available
  if (trainer.specialty && trainer.specialty.length > 0) {
    return `${trainer.full_name} - Entrenador personal en ${trainer.location_city}. Especialista en ${trainer.specialty[0]}${trainer.specialty[1] ? ' y ' + trainer.specialty[1] : ''}. Resultados garantizados. Primera sesión gratis.`.substring(0, 160);
  }
  
  // Format 2: Certifications available
  if (trainer.certifications && trainer.certifications.length >= 2) {
    return `${trainer.full_name} en ${trainer.location_city} - Entrenador certificado con formación profesional. Metodología personalizada para tus objetivos. Reserva tu evaluación.`.substring(0, 160);
  }
  
  // Format 3: Bio available
  if (trainer.bio) {
    const cleanBio = trainer.bio.substring(0, 70);
    return `${trainer.full_name} ${trainer.location_city}: ${cleanBio}. Entrenador personal profesional. Primera clase gratis.`.substring(0, 160);
  }
  
  // Format 4: Generic
  return `${trainer.full_name} - Tu entrenador personal en ${trainer.location_city}. Metodología adaptada a tus metas. Resultados reales. Prueba sin compromiso.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for sport professionals
 * Name first, no rankings/numbers - emphasizes achievements
 */
export const generateSportProfessionalMetaDescription = (athlete: SportProfessionalData): string => {
  const categoryName = athlete.category
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const tail = slugQualifierTail(athlete.slug, athlete.full_name);
  const nameWithTail = `${athlete.full_name}${tail}`;

  // Format 1: Achievements available
  if (athlete.achievements && athlete.achievements.length > 0) {
    return `${nameWithTail} - ${categoryName} profesional de ${athlete.location_country}. ${athlete.achievements[0].substring(0, 60)}. Conoce su trayectoria completa.`.substring(0, 160);
  }

  // Format 2: Bio available
  if (athlete.bio) {
    return `${nameWithTail} - ${categoryName} en ${athlete.location_city || athlete.location_country}: ${athlete.bio.substring(0, 70)}. Descubre su carrera deportiva.`.substring(0, 160);
  }

  // Format 3: Generic
  return `${nameWithTail} - Profesional de ${categoryName} en ${athlete.location_country}. Carrera destacada y logros nacionales. Inspírate con su historia.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for videos
 * Title first, no view counts/emojis - clear value proposition
 */
export const generateVideoMetaDescription = (video: VideoData): string => {
  // Use existing description if well-crafted
  if (video.descripcion && video.descripcion.length > 60 && video.descripcion.length <= 160) {
    return video.descripcion;
  }
  
  const categoria = video.categoria || 'Fitness';
  
  // Format 1: Category available
  if (video.categoria) {
    return `${video.titulo} - Tutorial de ${video.categoria}. Aprende técnicas profesionales paso a paso. Video completo y gratuito para todos los niveles.`.substring(0, 160);
  }
  
  // Format 2: Description available
  if (video.descripcion) {
    const cleanDesc = video.descripcion.substring(0, 80);
    return `${video.titulo}: ${cleanDesc}. Video tutorial completo. Aprende gratis.`.substring(0, 160);
  }
  
  // Format 3: City available
  if (video.ciudad) {
    return `${video.titulo} - Video tutorial ${categoria} grabado en ${video.ciudad}. Técnicas profesionales explicadas paso a paso. Contenido gratuito.`.substring(0, 160);
  }
  
  // Format 4: Generic
  return `${video.titulo} - Video tutorial fitness. Técnicas profesionales explicadas paso a paso. Contenido gratuito para mejorar tu entrenamiento.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for articles
 * Title first, no reading times/emojis - clear value proposition
 */
export const generateArticleMetaDescription = (article: ArticleData): string => {
  // Use existing meta_description if well-crafted
  if (article.meta_description && article.meta_description.length > 60 && article.meta_description.length <= 160) {
    return article.meta_description;
  }
  
  const category = article.category || 'Fitness';
  
  // Format 1: Tags available
  if (article.tags && article.tags.length > 0) {
    return `${article.title} - Guía completa sobre ${article.tags[0]}${article.tags[1] ? ' y ' + article.tags[1] : ''}. Consejos de expertos verificados. Lee el artículo completo.`.substring(0, 160);
  }
  
  // Format 2: Summary available
  if (article.summary) {
    const cleanSummary = article.summary.substring(0, 80);
    return `${article.title}: ${cleanSummary}. Artículo profesional con información verificada.`.substring(0, 160);
  }
  
  // Format 3: Category specific
  if (category) {
    return `${article.title} - Artículo experto de ${category}. Guía profesional con consejos prácticos. Información verificada por especialistas.`.substring(0, 160);
  }
  
  // Format 4: Generic
  return `${article.title} - Artículo completo con consejos profesionales y guías prácticas. Información verificada por expertos. Lectura gratuita.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for landing pages
 * No years, no specific numbers - evergreen content
 */
export const generateLandingPageMetaDescription = (pageType: string, city?: string): string => {
  const descriptions: Record<string, string> = {
    'yoga': `Llena tu centro de yoga con alumnos nuevos cada mes. Sistema completo de captación con perfil premium. Sin permanencia. Prueba gratis con setup incluido.`,
    
    'pilates': `Duplica alumnos en tu estudio Pilates. Perfil profesional y leads cualificados. Ya lo usan cientos de centros. Primera clase gratis con demo incluida.`,
    
    'crossfit': `Llena tu Box CrossFit con atletas captados automáticamente. Sistema usado por los mejores boxes. Métricas reales. Prueba gratis sin tarjeta.`,
    
    'artes-marciales': `Consigue alumnos nuevos para tu Dojo cada mes. Perfil premium y marketing automático. Sin permanencia. Primera semana gratis con soporte completo.`,
    
    'fitness': `Multiplica los socios de tu gimnasio. Plataforma líder para centros fitness. Genera leads diarios. Demo gratis disponible ahora.`,
    
    'los-mejores': `TOP Deportistas España: Vota por los mejores en todas las disciplinas. Ranking oficial verificado. Descubre campeones y estadísticas reales. Vota gratis.`,
    
    'entrenadores': `Encuentra tu entrenador personal perfecto. Profesionales certificados en toda España. Compara precios, lee opiniones reales. Primera clase gratis.`,
    
    'nutricionistas': `Nutricionistas deportivos certificados en toda España. Planes personalizados con resultados reales. Compara precios y opiniones. Consulta gratis.`,
    
    'fisioterapeutas': `Fisioterapeutas deportivos TOP en España. Especialistas en lesiones con recuperación rápida garantizada. Opiniones verificadas. Primera consulta con descuento.`,
    
    'eventos': `Eventos deportivos en España: Maratones, torneos, competiciones. Inscripción online fácil. Filtra por deporte, ciudad y fecha. Encuentra tu reto.`,
    
    'videos': `Videos fitness y tutoriales: Ejercicios explicados paso a paso. Rutinas profesionales gratis. Aprende técnicas de expertos. Ver ahora.`,
    
    'articulos': `Blog fitness y nutrición: Artículos de expertos con consejos verificados. Guías completas de entrenamiento, dietas y salud. Lee gratis.`,
    
    'tiendas': `Tiendas deportivas en España: Equipamiento, ropa, suplementos. Compara precios y opiniones. Ofertas exclusivas. Encuentra cerca de ti.`,
    
    'bienestar': `Centros bienestar y spa verificados en España. Masajes, terapias, wellness. Sin comisiones para centros. Registra tu centro gratis.`,
    
    'directorio': `Directorio Deportivo España: Gimnasios, tiendas, entrenadores verificados. Opiniones reales. Encuentra lo tuyo en segundos.`,
    
    'cerca-de-mi': city 
      ? `${city}: Gimnasios cerca de ti con precios reales. Compara centros, lee opiniones, reserva online. Mapa en vivo. Visita hoy.`
      : `Encuentra tu gimnasio ideal con búsqueda geolocalizada. Centros verificados, opiniones reales, precios transparentes. Descubre cerca de ti.`,
  };
  
  return descriptions[pageType] || `Ekselencia - Plataforma Deportiva España: Gimnasios, entrenadores certificados, eventos deportivos. Todo en un clic. Empieza gratis ahora.`;
};

/**
 * Generates unique meta descriptions for nutritionist profiles
 * Name first, no numbers/emojis
 */
interface NutritionistData {
  name: string;
  city: string;
  bio?: string;
  specializations?: string[];
  years_experience?: number;
  session_price?: number;
  certifications?: string[];
}

export const generateNutritionistMetaDescription = (nutritionist: NutritionistData): string => {
  const { name, city, bio, specializations } = nutritionist;
  
  // Format 1: Specializations available
  if (specializations && specializations.length > 0) {
    return `${name} - Nutricionista en ${city}. Especialista en ${specializations.slice(0, 2).join(' y ')}. Planes personalizados con resultados comprobados. Consulta inicial gratuita.`.substring(0, 160);
  }
  
  // Format 2: Bio available
  if (bio) {
    const cleanBio = bio.substring(0, 70);
    return `${name} ${city}: ${cleanBio}. Nutricionista profesional. Consulta online disponible.`.substring(0, 160);
  }
  
  // Format 3: Generic
  return `${name} - Nutricionista profesional en ${city}. Planes de nutrición personalizados para tus objetivos. Resultados garantizados. Primera consulta gratis.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for physiotherapist profiles
 * Name first, no numbers/emojis
 */
interface PhysiotherapistData {
  name: string;
  city: string;
  bio?: string;
  specializations?: string[];
  years_experience?: number;
  session_price?: number;
  certifications?: string[];
}

export const generatePhysiotherapistMetaDescription = (physio: PhysiotherapistData): string => {
  const { name, city, bio, specializations } = physio;
  
  // Format 1: Specializations available
  if (specializations && specializations.length > 0) {
    return `${name} - Fisioterapeuta en ${city}. Experto en ${specializations.slice(0, 2).join(' y ')}. Tratamiento personalizado con resultados garantizados. Pide tu cita.`.substring(0, 160);
  }
  
  // Format 2: Bio available
  if (bio) {
    const cleanBio = bio.substring(0, 70);
    return `${name} ${city}: ${cleanBio}. Fisioterapeuta deportivo profesional. Recuperación garantizada.`.substring(0, 160);
  }
  
  // Format 3: Generic
  return `${name} - Fisioterapeuta profesional en ${city}. Rehabilitación deportiva y tratamiento personalizado. Primera sesión con descuento.`.substring(0, 160);
};

/**
 * Generates unique meta descriptions for store profiles
 * Name first, no numbers/emojis
 */
interface StoreData {
  name: string;
  city: string;
  description?: string;
  categories?: string[];
  address?: string;
}

export const generateStoreMetaDescription = (store: StoreData): string => {
  const { name, city, description, categories, address } = store;
  
  // Format 1: Categories available
  if (categories && categories.length > 0) {
    return `${name} en ${city} - Tienda deportiva especializada en ${categories.slice(0, 2).join(' y ')}. Productos de calidad, atención profesional. Visítanos.`.substring(0, 160);
  }
  
  // Format 2: Description available
  if (description) {
    const cleanDesc = description.substring(0, 70);
    return `${name} ${city}: ${cleanDesc}. Tienda deportiva con los mejores productos. Ven a conocernos.`.substring(0, 160);
  }
  
  // Format 3: Address available
  if (address) {
    return `${name} - Tienda deportiva en ${address.split(',')[0]}, ${city}. Equipamiento profesional y asesoramiento experto. Te esperamos.`.substring(0, 160);
  }
  
  // Format 4: Generic
  return `${name} - Tienda deportiva en ${city}. Equipamiento, ropa y accesorios de calidad. Atención personalizada. Visítanos hoy.`.substring(0, 160);
};

/**
 * Truncates and cleans description to fit within character limit
 */
export const sanitizeMetaDescription = (description: string, maxLength: number = 160): string => {
  // Remove HTML tags
  let clean = description.replace(/<[^>]*>/g, '');
  
  // Remove extra whitespace
  clean = clean.replace(/\s+/g, ' ').trim();
  
  // Truncate if needed
  if (clean.length > maxLength) {
    clean = clean.substring(0, maxLength - 3) + '...';
  }
  
  return clean;
};

// Export all utility functions
export type { ListingData, EventData, TrainerData, SportProfessionalData, VideoData, ArticleData, NutritionistData, PhysiotherapistData, StoreData };
